import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges, TemplateRef, TrackByFunction, ViewChild, ChangeDetectorRef, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MaterialModule } from '../../material/material.module';
import { PagingService } from '../../services/paging/paging.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Table } from '../../models/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PagingComponent } from '../paging/paging.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Video } from '../../models/video';
import { PlayerMediaComponent } from '../player-media/player-media.component';
import { PipesModule } from '../../pipes/pipes.module';
import { DirectiveModule } from '../../directives/directive.module';
import { PermissionsEnum } from '../../enums/permissions.enum';
import { isNullOrUndefined, isObject, isValidFunction } from '@pineapplelab/util';
import { firstValueFrom } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { GenericAlertPopupComponent } from '../generic-alert-popup/generic-alert-popup.component';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MaterialModule,
    FormsModule,
    MatSlideToggleModule,
    DragDropModule,
    PagingComponent,
    MatProgressSpinnerModule,
    PipesModule,
    DirectiveModule
  ],
  providers: [PagingService],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableComponent implements OnInit, OnChanges {

  displayedColumns: string[] = [];
  selectedItemsInTable: any[] = [];
  showSpinner: boolean = false;
  PermissionsEnum = PermissionsEnum;
  expandedElement: any;
  PublishStatusEnum = PublishStatusEnum;

  @Input() tooltipText: string | null = null;
  @ViewChild('checkbox', { static: true }) checkbox: TemplateRef<any>;
  @ViewChild('toggle', { static: true }) toggle: TemplateRef<any>;
  @ViewChild('draggable', { static: true }) draggable: TemplateRef<any>;
  @ViewChild('headerTemplate', { static: true }) headerTemplate: TemplateRef<any>;

  @ViewChild('actionButton', { static: true }) actionButton: TemplateRef<any>;
  @ViewChild('deleteBtn', { static: true }) deleteBtn: TemplateRef<any>;
  @ViewChild('matTableRef') matTableRef: MatTable<any>;

  @Input() dataSource: Object[] = [];
  @Input() table: Table<any>;
  @Input() preselectedItems: any[] = [];

  @Input() editModalTemplate: TemplateRef<any>;
  // Extra context merged into the edit dialog's data, for form components that need more than the
  // row itself (e.g. the ids a chapter needs to load its retake quizzes from a subcollection).
  @Input() extraDialogData: Record<string, any> = {};
  @Input() formComponent: ComponentType<any>
  @Input() editModalConfig: MatDialogConfig = null;

  @Input() isPlay: boolean = false;
  @Input() isEdit: boolean = false;
  @Input() isDownload: boolean = false;
  @Input() isDeleted: boolean = false;
  @Input() isEditQuestion: boolean = false;
  @Input() isEditChapter: boolean = false;
  @Input() showEditWarning: boolean = false;
  /** IDs of chapters whose content (video/quiz) is locked because the course has enrollments. */
  @Input() lockedContentIds: string[] = [];
  @Input() toggleTooltipText: string | null = null;
  @Input() actionColumnTemplate: TemplateRef<any> = null;
  @Input() expandable: boolean = false;
  @Input() expandableTemplate: TemplateRef<any>;
  /** Row identity for mat-table; without it a fresh dataSource array rebuilds every row. */
  @Input() trackBy: TrackByFunction<any>;

  @Output() reOrganizedData = new EventEmitter<any[]>();
  @Output() selectedItems = new EventEmitter<any[] | any>();
  @Output() selectedEditItem = new EventEmitter<any[] | any>();
  @Output() toggleChanged = new EventEmitter<any>();
  @Output() onEditModalClosed = new EventEmitter<any>();

  columnsToDisplayWithExpand: string[] = [];

  private dialog = inject(MatDialog);

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.showSpinner = true;
    if (this.table) {
      this.initializeTable();
    }
    if (this.preselectedItems.length) {
      this.selectedItemsInTable = [...this.preselectedItems];
    }
    if (this.expandable) {
      this.initializeExpandableTableConfig();
    }
    this.showSpinner = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.showSpinner = true;
    if (changes['table'] && !changes['table'].firstChange) {
      this.initializeTable();
    }
    this.showSpinner = false;
  }

  private initializeExpandableTableConfig(): void {
    const columnFields = this.table.columns.map(column => column?.field);
    this.columnsToDisplayWithExpand = [...columnFields, 'expand'];
  }

  initializeTable(): void {
    if (this.table?.columns) {
      this.table.columns = this.table.columns.filter(column => column.field !== 'selectable' && column.field !== 'draggable' && column.field !== 'deleteBtn');
    }
    if (this.table?.actions && !this.columnExists('actions')) {
      this.table.columns.push({ label: '', field: 'actions' });
    }
    if (this.table?.selectable && this.table.type != 'none') {
      if (this.table?.type === 'checkbox' && !this.columnExists('selectable')) {
        this.table?.columns.splice(0, 0, { field: 'selectable', label: '', template: this.checkbox });
      }
      if (this.table?.type === 'toggle' && !this.columnExists('selectable')) {
        const toggleCondition = this.table.columns.find(item => isValidFunction(item?.disableColumnCondition));
        this.table?.columns.splice(0, 0, { field: 'selectable', label: 'general.active', template: this.toggle, disableColumnCondition: toggleCondition?.disableColumnCondition });
      }
      if (this.table?.type === 'draggable' && !this.columnExists('draggable')) {
        this.table?.columns.splice(0, 0, { field: 'draggable', label: 'general.order', template: this.draggable, headerTemplate: this.headerTemplate });

        if (!this.columnExists('deleteBtn') && this.table?.deleteBtn) {
          this.table.columns.push({ field: 'deleteBtn', label: '', template: this.deleteBtn });
        }
      }
    }

    this.displayedColumns = this.table?.columns
      .filter(column => column.field !== 'actions' || (column.field === 'actions' && this.table.actions))
      .map(column => column.field);

    setTimeout(() => {
      if (!this.dataSource?.length) {
        this.showSpinner = false;
      }
    }, 2000);

    this.cdr.detectChanges();
  }

  private columnExists(fieldName: string): boolean {
    return this.table.columns.some(column => column.field === fieldName);
  }

  selectedItem(event: any, item: any) {
    if (event) {
      if (event?.checked) {
        if (!this.selectedItemsInTable.some(selectedItem => selectedItem.id === item.id)) {
          this.selectedItemsInTable.push(item);
        }
      } else {
        this.selectedItemsInTable = this.selectedItemsInTable.filter(selectedItem => selectedItem.id !== item.id);
      }
      this.selectedItems.emit(this.selectedItemsInTable);
    } else {
      this.selectedItems.emit(item);
    }
    this.showSpinner = false;
  }

  selectedItemToDelete(item: any, index?: number) {
    let data = { item, index };
    this.selectedItems.emit(data);
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.dataSource, event.previousIndex, event.currentIndex);
    this.matTableRef.renderRows();
    this.reOrganizedData.emit(this.dataSource);

  }

  get isSelected(): (item: any) => boolean {
    return (item: any) => this.selectedItemsInTable.some(selectedItem => selectedItem.id === item.id);
  }

  toggleChange(event: any, element: any) {
    this.toggleChanged.emit(event);
  }

  async openEditModal(element: any) {
    if (this.formComponent) {
      let config = this.editModalConfig && isObject(this.editModalConfig) ? { data: element, ...this.editModalConfig } : { data: element };
      const dialogRef = await firstValueFrom(this.dialog.open(this.formComponent, config).afterClosed());
      this.onEditModalClosed.emit(dialogRef);
    } else {
      console.error('Template Reference not provided!')
    }
  }

  async editChapter(element: any) {
    if (this.showEditWarning) {
      const dialogRef = this.dialog.open(GenericAlertPopupComponent, {
        minWidth: '30vw',
        maxWidth: '40vw',
        minHeight: '15vw',
        data: {
          title: 'general.warning',
          description: 'course-creation.edit_chapter_content',
          dismissBtnText: 'general.dismiss',
          acceptBtnText: 'general.edit',
          btnColor: 'primary'
        }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) return;
    }

    await this.openEditDialog(element);
  }

  private async openEditDialog(element: any) {
    if (!this.formComponent) {
      console.error('Template Reference not provided!');
      return;
    }

    const dialogRef = this.dialog.open(this.formComponent, {
      width: '65%',
      height: '86%',
      data: {
        ...this.extraDialogData,
        chapter: element,
        isEdit: true,
        isTranslation: false,
        lockContent: this.lockedContentIds?.includes(element?.id) ?? false,
      }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    this.selectedEditItem.emit(result);
  }

  editQuestion(element: any, index: number) {
    let data = { element, index };
    this.selectedEditItem.emit(data);
  }

  playVideo(element: Video) {
    const dialogRef = this.dialog.open(PlayerMediaComponent, {
      width: '60%',
      maxHeight: '945px',
      maxWidth: '1140px',
      data: element
    });
  }

  onRowClick(element: any): void {
    if (this.table?.selectable && this.table?.type === 'none') {
      this.selectedItem(null, element);
    }
    if (this.expandable) {
      this.expandedElement = this.expandedElement === element ? null : element;
    }
  }

  disabledToggle(element: any): boolean {
    if (element?.isDefault) return true;
    if (!element?.__isFullTranslated) element.active = false;

    const isMissingData = !element?.name;
    const isCourseMismatch = element?.__selectedCoursesCounter !== element?.__translatedCoursesCounter;
    if (isMissingData || isCourseMismatch) return true;

    const hasChaptersWithoutTranslation = element?.chapters && !element?.translated;
    if (hasChaptersWithoutTranslation) return true;

    return false;
  }

  rerender(): void {
	this.matTableRef.renderRows();
  }
}
